import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import React, { FC, useContext, useEffect, useState } from 'react';

import { deleteChannelActivator } from '../../api/channels/delete-channel-activator';
import editChannelActivator from '../../api/channels/edit-channel-activator';
import { AppContext } from '../../AppContext';
import Input from '../../common/inputs/Input';
import ThreeDotsMenu from '../../common/threeDotsMenu/three-dots-menu.component';
import { BotContext } from '../bot.context';
import { FlowTypes, IFlowWithActivatorsAndFolder } from '../flows/interfaces';
import ModalScriptInfo from '../script/modal-script.info';

import CreateEditChannelActivator from './create-edit-channel-activator';
import {
  IChannelActivatorWithParams,
  ITelegramChannelWithActivators,
  UpdateChannelActivatorWithParams,
} from './interfaces';

interface ChannelActivatorsProps {
  channel: ITelegramChannelWithActivators;
  flows: IFlowWithActivatorsAndFolder[];
  onCreateActivator: (
    newChannelActivator: Omit<
      IChannelActivatorWithParams,
      | 'createdAt'
      | 'updatedAt'
      | '_id'
      | 'channel'
      | 'hidden'
      | 'inviteLink'
      | 'joinRequestsCount'
    >,
  ) => void;
  getChannels: () => void;
}

const ChannelActivators: FC<ChannelActivatorsProps> = ({
  channel,
  flows,
  onCreateActivator,
  getChannels,
}) => {
  const { bot } = useContext(BotContext);
  const { openConfirmationDialog } = useContext(AppContext);

  const [selectedActivator, setSelectedActivator] =
    useState<IChannelActivatorWithParams | null>(null);
  const [filteredActivators, setFilteredActivators] = useState<
    IChannelActivatorWithParams[] | null
  >(null);
  const [creatingNewActivator, setCreatingNewActivator] = useState(false);
  const [scriptModalOpen, setScriptModalOpen] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const [isCopyActivator, setIsCopyActivator] = useState<boolean>(false);
  const [filterInput, setFilterInput] = useState<string>('');

  const onOpenScriptModal = () => {
    setScriptModalOpen(true);
  };

  const onCloseScriptModal = () => {
    setScriptModalOpen(false);
  };

  const onEditActivator = (
    activatorToUpdate: UpdateChannelActivatorWithParams,
  ) => {
    if (!selectedActivator || !bot) return;

    editChannelActivator(
      bot?._id,
      channel._id,
      selectedActivator?._id,
      activatorToUpdate,
    ).then(getChannels);
  };

  const onCopyActivator = (activator: IChannelActivatorWithParams) => {
    setSelectedActivator(activator);
    setIsCopyActivator(true);
  };

  const onResetSelectedActivator = () => {
    setSelectedActivator(null);
  };

  const openProjectMenu = (
    e: React.SyntheticEvent,
    activator: IChannelActivatorWithParams,
  ) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSelectedActivator(activator);
  };

  useEffect(() => {
    setSelectedActivator(
      channel.activators.find(
        (activator) => activator._id === selectedActivator?._id,
      ) ?? null,
    );
  }, [channel]);

  useEffect(() => {
    setIsCopyActivator(false);
  }, [selectedActivator, creatingNewActivator]);

  useEffect(() => {
    setFilteredActivators(
      channel?.activators.filter((activator) =>
        activator.name.toLowerCase().includes(filterInput.toLowerCase()),
      ),
    );
  }, [channel, filterInput]);
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="10px"
      sx={{
        width: '100%',
        padding: '16px',
        borderRadius: '12px',
        backgroundColor: 'grey.14',
        border: '1px solid',
        borderColor: 'grey.10',
        maxWidth: '787px',
        height: '500px',
        margin: '0 auto',
      }}
    >
      <Stack
        sx={{
          width: '30%',
          height: '100%',
          alignSelf: 'start',
        }}
      >
        <Stack flexDirection="row" gap="5px">
          <Input
            value={filterInput}
            handleChangeSearch={(e) => setFilterInput(e.target.value)}
            fullWidth={true}
          />
        </Stack>
        {Boolean(filteredActivators?.length) ? (
          <Stack
            gap="8px"
            sx={{
              display: channel.activators.length ? 'flex' : 'none',
              overflow: 'hidden',
              mt: '10px',
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {filteredActivators
              ?.filter((item) => item.name !== 'api')
              ?.map((activator) => (
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  sx={{
                    border: '1px solid ',
                    borderColor: 'grey.15',
                    borderRadius: '8px',
                    transition: 'all .1s',
                    padding: '11px 16px',
                    cursor: 'pointer',
                    backgroundColor:
                      activator._id === selectedActivator?._id &&
                      !isCopyActivator
                        ? 'grey.10'
                        : 'grey.15',

                    color:
                      activator._id === selectedActivator?._id &&
                      !isCopyActivator
                        ? 'green.2'
                        : 'grey.1',
                  }}
                  key={activator._id}
                  onClick={() => {
                    setSelectedActivator(activator);
                    setCreatingNewActivator(false);
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '18px',
                    }}
                  >
                    {activator.name}
                  </Typography>
                  <Stack onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      onClick={(e) => openProjectMenu(e, activator)}
                      sx={{
                        alignItems: 'start',
                        height: 'fit-content',
                        padding: '0',
                        color: 'grey.5',
                      }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                    <ThreeDotsMenu
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(null)}
                      anchor={menuAnchor}
                      border={'1px solid'}
                      borderColor={'grey.12'}
                      backgroundColor={'grey.15'}
                      paddingMenu={'0px'}
                      paddingMenuItem={'10px 20px'}
                      borderRadius={'8px'}
                      marginDivider={'0 12px'}
                      menuItems={[
                        [
                          {
                            text: t('common.copy'),
                            textSx: {
                              color: 'grey.5',
                              fontWeight: 500,
                              fontSize: '16px',
                            },
                            renderIcon: () => (
                              <ContentCopyIcon
                                sx={{
                                  color: 'grey.5',
                                }}
                              />
                            ),
                            onClick: () => {
                              onCopyActivator(activator);
                            },
                          },
                        ],
                        [
                          {
                            text: t('common.delete'),
                            textSx: {
                              color: 'red.2',
                              fontWeight: 500,
                              fontSize: '16px',
                            },
                            renderIcon: () => (
                              <DeleteOutlineRoundedIcon
                                sx={{ color: 'red.2' }}
                              />
                            ),
                            onClick: () => {
                              openConfirmationDialog(
                                `${t('activators.deleteActivatorLink')} ${
                                  selectedActivator?.name
                                }?`,
                                t('common.delete'),
                                () => {
                                  deleteChannelActivator(
                                    bot?._id ?? '',
                                    channel._id,
                                    selectedActivator?._id ?? '',
                                  ).then(getChannels);
                                },
                              );
                            },
                          },
                        ],
                      ]}
                    />
                  </Stack>
                </Stack>
              ))}
          </Stack>
        ) : (
          <Stack sx={{ mt: '20px' }}>
            <Typography sx={{ textAlign: 'center' }}>
              {t('common.notFound')}
            </Typography>
          </Stack>
        )}
        <Stack
          sx={{
            mt: 'auto',
          }}
        >
          <Button
            onClick={onOpenScriptModal}
            startIcon={
              <svg
                width="18"
                height="13"
                viewBox="0 0 18 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.969712 7.52992L3.96971 10.5299C4.0389 10.6016 4.12166 10.6587 4.21316 10.698C4.30466 10.7373 4.40308 10.758 4.50266 10.7589C4.60225 10.7597 4.70101 10.7407 4.79318 10.703C4.88535 10.6653 4.96909 10.6096 5.03951 10.5392C5.10993 10.4688 5.16562 10.3851 5.20333 10.2929C5.24104 10.2007 5.26002 10.102 5.25915 10.0024C5.25828 9.90279 5.23759 9.80437 5.19829 9.71287C5.15898 9.62137 5.10184 9.53861 5.03021 9.46942L2.56046 6.99967L5.03021 4.52992C5.10184 4.46074 5.15898 4.37798 5.19829 4.28648C5.23759 4.19497 5.25828 4.09656 5.25915 3.99697C5.26002 3.89739 5.24104 3.79863 5.20333 3.70646C5.16562 3.61428 5.10993 3.53055 5.03951 3.46013C4.96909 3.38971 4.88535 3.33402 4.79318 3.29631C4.70101 3.2586 4.60225 3.23962 4.50266 3.24048C4.40308 3.24135 4.30466 3.26204 4.21316 3.30135C4.12166 3.34065 4.0389 3.39779 3.96971 3.46942L0.969712 6.46942C0.829109 6.61007 0.750122 6.8008 0.750122 6.99967C0.750122 7.19855 0.829109 7.38928 0.969712 7.52992ZM14.0302 3.46942C13.961 3.39779 13.8783 3.34065 13.7868 3.30135C13.6953 3.26204 13.5968 3.24135 13.4973 3.24048C13.3977 3.23962 13.2989 3.2586 13.2067 3.29631C13.1146 3.33402 13.0308 3.38971 12.9604 3.46013C12.89 3.53055 12.8343 3.61428 12.7966 3.70646C12.7589 3.79863 12.7399 3.89739 12.7408 3.99697C12.7416 4.09656 12.7623 4.19497 12.8016 4.28648C12.8409 4.37798 12.8981 4.46074 12.9697 4.52992L15.4395 6.99967L12.9697 9.46942C12.8981 9.53861 12.8409 9.62137 12.8016 9.71287C12.7623 9.80437 12.7416 9.90279 12.7408 10.0024C12.7399 10.102 12.7589 10.2007 12.7966 10.2929C12.8343 10.3851 12.89 10.4688 12.9604 10.5392C13.0308 10.6096 13.1146 10.6653 13.2067 10.703C13.2989 10.7407 13.3977 10.7597 13.4973 10.7589C13.5968 10.758 13.6953 10.7373 13.7868 10.698C13.8783 10.6587 13.961 10.6016 14.0302 10.5299L17.0302 7.52992C17.1708 7.38928 17.2498 7.19855 17.2498 6.99967C17.2498 6.8008 17.1708 6.61007 17.0302 6.46942L14.0302 3.46942ZM9.77921 1.54417L6.77921 12.0442C6.7246 12.2353 6.74811 12.4403 6.84459 12.6142C6.94106 12.788 7.10261 12.9164 7.29371 12.9712C7.3609 12.9897 7.43026 12.9993 7.49996 12.9997C7.66295 12.9996 7.82146 12.9463 7.9515 12.8481C8.08155 12.7499 8.17605 12.6119 8.22071 12.4552L11.2207 1.95517C11.252 1.85929 11.2636 1.75808 11.2548 1.6576C11.2461 1.55712 11.2172 1.45944 11.1698 1.3704C11.1224 1.28136 11.0576 1.20279 10.9792 1.13939C10.9007 1.07599 10.8103 1.02905 10.7133 1.0014C10.6163 0.973746 10.5147 0.965938 10.4147 0.978444C10.3146 0.99095 10.2181 1.02351 10.1309 1.07418C10.0437 1.12485 9.96757 1.19259 9.90715 1.27335C9.84673 1.3541 9.80321 1.44621 9.77921 1.54417Z"
                  fill="#CAFA44"
                />
              </svg>
            }
            sx={{
              mt: '10px',
              border: 'none',
              color: 'green.2',
              backgroundColor: 'transparent',
              mb: '10px',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('common.activators.modalScript')}
          </Button>
          <Button
            className="blue"
            fullWidth
            onClick={() => {
              setSelectedActivator(null);
              setCreatingNewActivator(true);
            }}
          >
            {t('activators.newLink')}
          </Button>
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: '1px',
          height: '100%',
          backgroundColor: 'grey.10',
        }}
      />
      <Stack
        sx={{
          width: '68%',
          alignSelf: 'start',
          height: '100%',
        }}
      >
        {!selectedActivator && !creatingNewActivator && (
          <Stack
            sx={{
              width: '100%',
              height: '100%',
              mt: '16px',
            }}
          >
            <Typography
              sx={{
                color: 'grey.5',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              {t('channels.channelActivators.emptySelect')}
            </Typography>
          </Stack>
        )}
        {(creatingNewActivator || selectedActivator || isCopyActivator) && (
          <CreateEditChannelActivator
            channelFlows={flows.filter((flow: IFlowWithActivatorsAndFolder) =>
              flow.type.includes(FlowTypes.join || FlowTypes.leave),
            )}
            isCopyActivator={isCopyActivator}
            onResetSelectedActivator={onResetSelectedActivator}
            onCreateActivator={onCreateActivator}
            creatingNewActivator={creatingNewActivator}
            selectedActivator={selectedActivator}
            onEditActivator={onEditActivator}
          />
        )}
      </Stack>
      <ModalScriptInfo
        scriptModalOpen={scriptModalOpen}
        handleScriptModalClose={onCloseScriptModal}
        type="channel"
        channelId={channel._id}
        flows={flows}
        botId={channel.telegramBot}
      />
    </Stack>
  );
};

export default ChannelActivators;
